/**
 * Google Maps for the admin console and the employee portal.
 *
 * Replaces OpenStreetMap's public tile and search servers, which are volunteer-
 * run and whose usage policies forbid exactly what a commercial product does —
 * they block offending networks with a 403 "Access blocked" tile, which is how
 * this came to light. Google is also what the mobile app uses, so both clients
 * now show the same map and the same search results.
 *
 * The key is a **browser key**: it ships in the bundle and anyone can read it.
 * That is normal for Maps JavaScript — the protection is the key's HTTP-referrer
 * restriction in Google Cloud, not secrecy. It must be a separate key from the
 * mobile app's, which is restricted to app signatures rather than referrers.
 *
 * `VITE_*` values are baked in at **build** time, so production only has a map
 * if the deploy workflow passes these through from repository variables.
 */
export const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "").trim();

/**
 * Advanced markers (draggable pins, custom HTML pins) need a Map ID. For those
 * alone Google's documentation says a real one is not needed and `DEMO_MAP_ID`
 * may be used, so that is the fallback. Create one under Map Management — in
 * the same project as the key — only for cloud-based styling or vector maps.
 */
export const GOOGLE_MAPS_MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID ?? "").trim() || "DEMO_MAP_ID";

export const hasGoogleMapsKey = GOOGLE_MAPS_API_KEY.length > 0;

/** Default centre when nothing better is known: Kuala Lumpur. */
export const DEFAULT_CENTER = { lat: 3.1569, lng: 101.7123 };

/**
 * Google Maps draws shapes itself and cannot resolve `var(--primary)`, so read
 * the theme token and hand it a literal colour. Falls back to the brand blue.
 */
export function primaryColor(): string {
  if (typeof window === "undefined") return "#1d63ed";
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const parts = raw.split(/\s+/);
  if (parts.length < 3) return "#1d63ed";
  return `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})`;
}
